import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getBlockStatus, getSoftBlockStatus, BlockStatus } from "src/apis/upsell";
import { getUserCompanyId, isUserAdministrator, isUserCompany } from "src/utils/auth";

export type CompanyBlocksContextValue = {
  blockStatus?: BlockStatus | null;
  softBlockStatus?: BlockStatus | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
  isActionRestricted: (action: string) => boolean;
};

const initialValue: CompanyBlocksContextValue = {
  blockStatus: undefined,
  softBlockStatus: undefined,
  isLoading: false,
  refetch: async () => {},
  isActionRestricted: () => false,
};

const CompanyBlocksContext = createContext<CompanyBlocksContextValue>(initialValue);

export const CompanyBlocksProvider: FC<PropsWithChildren> = ({ children }) => {
  const [blockStatus, setBlockStatus] = useState<BlockStatus | null>();
  const [softBlockStatus, setSoftBlockStatus] = useState<BlockStatus | null>();
  const [isLoading, setIsLoading] = useState(false);

  const isCompanyUser = useMemo(() => isUserCompany() && !isUserAdministrator(), []);

  const fetchStatuses = useCallback(async () => {
    if (!isCompanyUser) return;

    const companyId = getUserCompanyId();
    if (!companyId) return;

    setIsLoading(true);
    try {
      const [blockResponse, softBlockResponse] = await Promise.all([
        getBlockStatus(companyId),
        getSoftBlockStatus(companyId),
      ]);

      setBlockStatus(blockResponse);
      setSoftBlockStatus(softBlockResponse);
    } catch (err) {
      console.error("Erro ao buscar status de bloqueio da empresa", err);
    } finally {
      setIsLoading(false);
    }
  }, [isCompanyUser]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const isActionRestricted = useCallback((action: string) => {
    if (!blockStatus?.is_blocked) return false;

    return blockStatus?.restricted_actions?.includes(action);
  }, [blockStatus]);

  const value = useMemo(() => ({
    blockStatus,
    softBlockStatus,
    isLoading,
    refetch: fetchStatuses,
    isActionRestricted,
  }), [blockStatus, softBlockStatus, isLoading, fetchStatuses, isActionRestricted]);

  return (
    <CompanyBlocksContext.Provider value={value}>
      {children}
    </CompanyBlocksContext.Provider>
  );
};

export const useCompanyBlocks = () => useContext(CompanyBlocksContext);
